import {
    ApplicationRef,
    createComponent,
    EnvironmentInjector, Injectable, InjectionToken, Injector,
    NgZone,
    ViewContainerRef
} from '@angular/core';
import { LIFECYCLE_DID_ENTER, LIFECYCLE_DID_LEAVE, LIFECYCLE_WILL_ENTER, LIFECYCLE_WILL_LEAVE, LIFECYCLE_WILL_UNLOAD } from '@inscomponents/core';
import { DelegateParams } from './delegate-params';

@Injectable({providedIn: 'root'})
export class FrameworkDelegate {
    public constructor(private zone: NgZone, private appRef: ApplicationRef) {}

    public create(environmentInjector: EnvironmentInjector, injector: Injector, location?: ViewContainerRef): AngularFrameworkDelegate {
        return new AngularFrameworkDelegate(environmentInjector, injector, location, this.appRef, this.zone);
    }
}

export class AngularFrameworkDelegate {
    private elRefMap = new WeakMap<HTMLElement, any>();
    private elEventsMap = new WeakMap<HTMLElement, () => void>();

    public constructor(
        private environmentInjector: EnvironmentInjector,
        private injector: Injector,
        private location: ViewContainerRef | undefined,
        private appRef: ApplicationRef,
        private zone: NgZone,
    ) {}

    public attachViewToDom(container: any, component: any, params?: any, cssClasses?: string[]): Promise<any> {
        return this.zone.run(() => {
            return new Promise(resolve => {
                const el = attachView(
                    this.zone,
                    this.environmentInjector,
                    this.injector,
                    this.location,
                    this.appRef,
                    this.elRefMap,
                    this.elEventsMap,
                    container,
                    component,
                    params,
                    cssClasses,
                );
                resolve(el);
            });
        });
    }

    public removeViewFromDom(_container: any, component: any): Promise<void> {
        return this.zone.run(() => {
            return new Promise(resolve => {
                const componentRef = this.elRefMap.get(component);
                if (componentRef) {
                    componentRef.destroy();
                    this.elRefMap.delete(component);
                    const unbindEvents = this.elEventsMap.get(component);
                    if (unbindEvents) {
                        unbindEvents();
                        this.elEventsMap.delete(component);
                    }
                }
                resolve();
            });
        });
    }
}

export const attachView = (
    zone: NgZone,
    environmentInjector: EnvironmentInjector,
    injector: Injector,
    location: ViewContainerRef | undefined,
    appRef: ApplicationRef,
    elRefMap: WeakMap<HTMLElement, any>,
    elEventsMap: WeakMap<HTMLElement, () => void>,
    container: any,
    component: any,
    params: any,
    cssClasses: string[] | undefined,
) => {
    const childInjector = Injector.create({
        providers: getProviders(params),
        parent: injector,
    });

    const componentRef = createComponent(component, {
        elementInjector: childInjector,
        environmentInjector,
    });

    const instance = componentRef.instance as any;
    const hostElement = componentRef.location.nativeElement;
    if (params) {
        for (const [key, value] of Object.entries(params)) {
            try {
                componentRef.setInput(key, value);
            } catch {
                instance[key] = value;
            }
        }
    }
    if (cssClasses) {
        for (const clazz of cssClasses) {
            hostElement.classList.add(clazz);
        }
    }
    const unbindEvents = bindLifecycleEvents(zone, instance, hostElement);
    container.appendChild(hostElement);

    if (!location) {
        appRef.attachView(componentRef.hostView);
    }
    componentRef.changeDetectorRef.reattach();
    elRefMap.set(hostElement, componentRef);
    elEventsMap.set(hostElement, unbindEvents);
    return hostElement;
};

const LIFECYCLES = [
    LIFECYCLE_WILL_ENTER,
    LIFECYCLE_DID_ENTER,
    LIFECYCLE_WILL_LEAVE,
    LIFECYCLE_DID_LEAVE,
    LIFECYCLE_WILL_UNLOAD,
] as const;

export const bindLifecycleEvents = (zone: NgZone, instance: any, element: HTMLElement) => {
    return zone.run(() => {
        const unregisters = LIFECYCLES.filter(eventName => typeof instance[eventName] === 'function').map(eventName => {
            const handler = (ev: any) => instance[eventName](ev.detail);
            element.addEventListener(eventName, handler);
            return () => element.removeEventListener(eventName, handler);
        });
        return () => unregisters.forEach(fn => fn());
    });
};

const DELEGATE_PARAMS_TOKEN = new InjectionToken<any>('DelagatorParamsToken');

const getProviders = (params: {[key: string]: any}) => {
    return [
        {
            provide: DELEGATE_PARAMS_TOKEN, useValue: params
        },
        {
            provide: DelegateParams, useFactory: provideDelegateParamsInjectable, deps: [DELEGATE_PARAMS_TOKEN]
        }
    ];
};

const provideDelegateParamsInjectable = (params: {[key: string]: any}) => {
    return new DelegateParams(params);
};
